import { Badge, DataGrid, DataGridColumn, Spinner, TooltipV2 } from '@cimplico/components';
import { Link } from 'react-router-dom';
import EmptyStateNew from '../../components/empty-state/EmptyStateNew';
import NoResults from '../../components/empty-state/NoResults';
import { ROW_SIZES } from '../../constants';
import { useCustomWorksheetTemplates } from '../../queries/use-custom-worksheet-templates';
import { CustomWorksheetTemplateOverview, CustomWorksheetTemplateStatus } from '../../types/custom-worksheet-v2';
import { calcRelativeTime, formatDateTime } from '../../utilities/formatting';
import CWTStatusBadge from '../custom-worksheet-template/CWTStatusBadge';

const columns: DataGridColumn[] = [
    {
        header: 'Name',
        alignment: 'left',
        width: '2fr',
    },
    {
        header: 'Tags',
        alignment: 'center',
        width: '1fr',
    },
    {
        header: 'Created By',
        alignment: 'left',
        width: '1fr',
    },
    {
        header: 'Last Updated',
        alignment: 'left',
        width: '1fr',
    },
];

type CustomWorksheetTemplatesTableProps = {
    search?: string;
    status?: CustomWorksheetTemplateStatus | 'ALL';
    onSelect?: (templates: CustomWorksheetTemplateOverview[]) => void;
};

function CustomWorksheetTemplatesTable({ search, status, onSelect }: CustomWorksheetTemplatesTableProps): JSX.Element {
    const templatesQuery = useCustomWorksheetTemplates({ search, status: status === 'ALL' ? undefined : status });

    const templates = templatesQuery?.data?.templates ?? [];
    const totalItems = templatesQuery?.data?.totalItems ?? 0;

    function handleSelect(ids: string[]): void {
        onSelect && onSelect(templates.filter((t) => ids.includes(t.id)));
    }

    return (
        <div className="w-full">
            <DataGrid
                onSelect={handleSelect}
                columns={columns}
                rowSizes={ROW_SIZES}
                totalRows={totalItems}
                isLoading={templatesQuery.isLoading}
                emptyMessage={
                    search ? (
                        <NoResults search={search} />
                    ) : (
                        <EmptyStateNew title={'No templates found'} helpText="Change filters to see available templates" />
                    )
                }
            >
                {templates.map((template) => (
                    <DataGrid.Row key={template.id} id={template.id}>
                        {/* Name */}
                        <div className="lg:flex items-center gap-2 py-2">
                            <Link
                                to={template.id}
                                className="font-medium text-indigo-600 dark:text-indigo-400 text-sm cursor-pointer break-word"
                            >
                                {template.name}
                            </Link>
                            <div className="flex items-center shrink-0">
                                <CWTStatusBadge status={template.status} />
                            </div>
                        </div>
                        {/* Tags */}
                        <div className="flex items-center gap-1">
                            <Link to={template.id} className="text-sm text-gray-700 hover:underline text-center">
                                {template.tags[0]}
                            </Link>
                            {template.tags.length > 1 && (
                                <span className="">
                                    <TooltipV2 message={template.tags.slice(1)}>
                                        <label className="text-sm text-gray-800">+{template.tags.length - 1}</label>
                                    </TooltipV2>
                                </span>
                            )}
                        </div>
                        {/* Created By */}
                        <p className="text-sm text-gray-700">
                            {template.createdBy ? `${template.createdBy.firstName} ${template.createdBy.lastName}` : '-'}
                        </p>
                        {/* Updated At */}
                        <TooltipV2 message={formatDateTime(template.updatedAt)}>
                            <p className="text-gray-700 text-sm">{calcRelativeTime(template.updatedAt)}</p>
                        </TooltipV2>
                    </DataGrid.Row>
                ))}
            </DataGrid>
        </div>
    );
}

export default CustomWorksheetTemplatesTable;
